import { BRICK_KIND } from '../entities/brickfield.js';
import { PADDLE_MODE, laserMuzzles } from '../entities/paddle.js';
import { BRICK_COLORS, PADDLE_COLORS, UI } from './palette.js';
import { capsuleColor } from '../systems/powerups.js';
import { drawText } from './font.js';

/** Every playfield entity, drawn with flat fills and hard bevels. */

export function drawBricks(ctx, field) {
  field.forEachAlive((brick) => drawBrick(ctx, brick));
}

function drawBrick(ctx, brick) {
  const colors = BRICK_COLORS[brick.code] || BRICK_COLORS.w;
  const { x, y, w, h } = brick;

  ctx.fillStyle = colors.base;
  ctx.fillRect(x, y, w, h);

  ctx.fillStyle = colors.light;
  ctx.fillRect(x, y, w, 2);
  ctx.fillRect(x, y, 2, h);

  ctx.fillStyle = colors.dark;
  ctx.fillRect(x, y + h - 2, w, 2);
  ctx.fillRect(x + w - 2, y, 2, h);

  if (brick.kind === BRICK_KIND.SILVER) {
    ctx.fillStyle = colors.light;
    for (let i = 5; i < h - 3; i += 4) ctx.fillRect(x + 4, y + i, w - 8, 1);
    // Damaged silver shows a crack down the middle.
    if (brick.hp < brick.maxHp) {
      ctx.fillStyle = colors.dark;
      ctx.fillRect(x + w / 2 - 1, y + 3, 2, h - 6);
      ctx.fillRect(x + w / 2 - 5, y + h / 2 - 1, 10, 2);
    }
  } else if (brick.kind === BRICK_KIND.GOLD) {
    ctx.fillStyle = colors.dark;
    for (let i = 0; i < w; i += 6) {
      ctx.fillRect(x + i + 2, y + 3, 2, h - 6);
    }
    ctx.fillStyle = colors.light;
    ctx.fillRect(x + 3, y + 3, 1, h - 6);
  }

  if (brick.flash > 0) {
    ctx.fillStyle = `rgba(255,255,255,${Math.min(0.85, brick.flash * 8)})`;
    ctx.fillRect(x, y, w, h);
  }
}

export function drawPaddle(ctx, paddle) {
  const left = paddle.cx - paddle.w / 2;
  const { y, h, w } = paddle;

  if (paddle.spawnAnim > 0) {
    // Materialising after a lost life: flicker in and out.
    const blink = Math.floor(paddle.spawnAnim * 24) % 2 === 0;
    ctx.globalAlpha = blink ? 0.35 : 0.9;
  }

  const capW = Math.min(12, w * 0.22);
  const capColor =
    paddle.mode === PADDLE_MODE.LASER
      ? PADDLE_COLORS.capLaser
      : paddle.mode === PADDLE_MODE.CATCH
        ? PADDLE_COLORS.capCatch
        : PADDLE_COLORS.cap;

  ctx.fillStyle = PADDLE_COLORS.core;
  ctx.fillRect(left, y, w, h);

  ctx.fillStyle = PADDLE_COLORS.shell;
  ctx.fillRect(left + capW, y, w - capW * 2, 4);
  ctx.fillStyle = PADDLE_COLORS.shellDark;
  ctx.fillRect(left + capW, y + h - 3, w - capW * 2, 3);

  ctx.fillStyle = capColor;
  ctx.fillRect(left, y + 1, capW, h - 2);
  ctx.fillRect(left + w - capW, y + 1, capW, h - 2);

  ctx.fillStyle = PADDLE_COLORS.seam;
  ctx.fillRect(left + capW, y + 5, 1, h - 8);
  ctx.fillRect(left + w - capW - 1, y + 5, 1, h - 8);

  if (paddle.mode === PADDLE_MODE.LASER) {
    ctx.fillStyle = PADDLE_COLORS.capLaser;
    for (const mx of laserMuzzles(paddle)) {
      ctx.fillRect(Math.round(mx) - 2, y - 5, 4, 5);
      ctx.fillStyle = '#ffd9d2';
      ctx.fillRect(Math.round(mx) - 1, y - 5, 2, 2);
      ctx.fillStyle = PADDLE_COLORS.capLaser;
    }
  } else if (paddle.mode === PADDLE_MODE.CATCH) {
    ctx.fillStyle = PADDLE_COLORS.capCatch;
    ctx.fillRect(left + capW, y - 2, w - capW * 2, 2);
  }

  ctx.globalAlpha = 1;
}

export function drawBalls(ctx, balls) {
  for (const ball of balls) {
    const trail = ball.trail;
    for (let i = 0; i < trail.length; i += 2) {
      const alpha = (i / trail.length) * 0.4;
      const size = 2 + (i / trail.length) * 3;
      ctx.fillStyle = `rgba(150,162,180,${alpha.toFixed(3)})`;
      ctx.fillRect(trail[i] - size / 2, trail[i + 1] - size / 2, size, size);
    }

    ctx.fillStyle = UI.ball;
    ctx.beginPath();
    ctx.arc(ball.x, ball.y, ball.r, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#aeb8c8';
    ctx.fillRect(ball.x - 1, ball.y + ball.r - 3, 2, 2);
  }
}

export function drawCapsules(ctx, capsules) {
  for (const capsule of capsules) {
    const color = capsuleColor(capsule.type);
    // Barrel roll: squash horizontally as it spins.
    const squash = Math.abs(Math.cos(capsule.spin)) * 0.75 + 0.25;
    const w = capsule.w * squash;
    const x = capsule.x - w / 2;
    const y = capsule.y - capsule.h / 2;

    ctx.fillStyle = '#0c0e12';
    ctx.fillRect(x - 1, y - 1, w + 2, capsule.h + 2);
    ctx.fillStyle = color;
    ctx.fillRect(x, y, w, capsule.h);
    ctx.fillStyle = 'rgba(255,255,255,0.28)';
    ctx.fillRect(x, y, w, 2);
    ctx.fillStyle = 'rgba(0,0,0,0.3)';
    ctx.fillRect(x, y + capsule.h - 2, w, 2);

    if (squash > 0.6) {
      ctx.globalAlpha = Math.min(1, (squash - 0.6) * 4);
      drawText(ctx, capsule.type, capsule.x, y + 4, { scale: 1, color: '#0b0d11', align: 'center' });
      drawText(ctx, capsule.type, capsule.x + 1, y + 4, {
        scale: 1,
        color: '#0b0d11',
        align: 'center',
      });
      ctx.globalAlpha = 1;
    }
  }
}

export function drawBolts(ctx, bolts) {
  for (const bolt of bolts) {
    ctx.fillStyle = '#ff8f6b';
    ctx.fillRect(bolt.x - bolt.w / 2, bolt.y, bolt.w, bolt.h);
    ctx.fillStyle = '#fff2e6';
    ctx.fillRect(bolt.x - 0.5, bolt.y + 1, 1, bolt.h - 2);
  }
}

export function drawDohShots(ctx, shots) {
  for (const shot of shots) {
    const wobble = Math.sin(shot.life * 14) * 0.8;
    ctx.fillStyle = '#2a0f30';
    ctx.beginPath();
    ctx.arc(shot.x, shot.y, shot.r + 1.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#c14fd8';
    ctx.beginPath();
    ctx.arc(shot.x, shot.y, shot.r + wobble * 0.3, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#f2c9ff';
    ctx.fillRect(shot.x - 1.5, shot.y - 2.5, 2, 2);
  }
}

export function drawParticles(ctx, particles) {
  for (const p of particles) {
    const alpha = Math.max(0, Math.min(1, p.life / p.maxLife));
    ctx.globalAlpha = alpha;
    ctx.fillStyle = p.color;
    ctx.fillRect(Math.round(p.x), Math.round(p.y), p.size, p.size);
  }
  ctx.globalAlpha = 1;
}
